import React, { useEffect } from "react";

import {
  faCircleInfo,
  faColumns,
  faHashtag,
  faPlus,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import {
  Control,
  FormProvider,
  useController,
  useFieldArray,
  useForm,
  useFormContext,
  useFormState,
} from "react-hook-form";

import { Label } from "src/base-components/Label";
import { TypeOption } from "src/base-components/SchemaTypeSelector";
import { Option, Select } from "src/base-components/Select";
import { Switch } from "src/base-components/Switch";
import {
  TableColumnConfigInput,
  TableColumnConfigInputDisplayConfig,
  TableCurrencyCode,
  TableEditMode,
} from "src/clients/case-management";
import { Button } from "src/design-system/Button";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon, IconButton } from "src/design-system/Icon";
import { Input } from "src/design-system/Input";
import { Modal } from "src/design-system/Modal";
import { Tooltip } from "src/design-system/Tooltip";
import { unslugify } from "src/utils/pythonUtils";

type DisplayType = TableColumnConfigInputDisplayConfig["type"];

type ColumnFormRow = {
  key: string;
  display_name: string;
  display_type: DisplayType | "none";
  currency_symbol: TableCurrencyCode;
  edit_mode: TableEditMode;
};

type ColumnConfigFormValues = {
  columns: ColumnFormRow[];
  defaultColumnsEditable: boolean;
};

type ColumnConfigModalProps = {
  isOpen: boolean;
  onClose: () => void;
  afterLeave: () => void;
  columns: { [key: string]: TableColumnConfigInput } | null | undefined;
  onSave: (
    columns: { [key: string]: TableColumnConfigInput },
    defaultColumnsEditable: boolean,
  ) => void;
  defaultColumnsEditable: boolean;
};

const DISPLAY_TYPES: DisplayType[] = [
  "string",
  "number",
  "boolean",
  "date",
  "datetime",
  "currency",
  "enum",
  "tags",
  "list",
  "object",
];

const DISPLAY_TYPE_OPTIONS: Option[] = [
  { key: "none", value: "From block input" },
  ...DISPLAY_TYPES.map((type) => ({
    key: type,
    value: (
      <TypeOption
        icon={type === "currency" ? faHashtag : undefined}
        label={type}
      />
    ),
  })),
];

const CURRENCY_OPTIONS: Option[] = [
  { key: "USD", value: "USD ($)", displayValue: "$" },
  { key: "EUR", value: "EUR (€)", displayValue: "€" },
  { key: "GBP", value: "GBP (£)", displayValue: "£" },
  { key: "BRL", value: "BRL (R$)", displayValue: "R$" },
  { key: "MXN", value: "MXN ($)", displayValue: "$" },
  { key: "CAD", value: "CAD ($)", displayValue: "$" },
  { key: "AUD", value: "AUD ($)", displayValue: "$" },
];

const EMPTY_ROW: ColumnFormRow = {
  key: "",
  display_name: "",
  display_type: "none",
  currency_symbol: TableCurrencyCode.USD,
  edit_mode: TableEditMode.EDITABLE,
};

const columnsToFormRows = (
  columns: { [key: string]: TableColumnConfigInput } | null | undefined,
): ColumnFormRow[] => {
  if (!columns) return [];

  return Object.entries(columns).map(([key, config]) => {
    const { display_config } = config;

    return {
      key,
      display_name: config.display_name ?? "",
      display_type: display_config?.type ?? "none",
      currency_symbol:
        display_config?.type === "currency"
          ? display_config.symbol
          : TableCurrencyCode.USD,
      edit_mode: config.edit_mode ?? TableEditMode.EDITABLE,
    };
  });
};

const formRowsToColumns = (
  rows: ColumnFormRow[],
): { [key: string]: TableColumnConfigInput } => {
  return Object.fromEntries(
    rows.map((row) => {
      const displayConfig: TableColumnConfigInputDisplayConfig | null =
        row.display_type === "none"
          ? null
          : row.display_type === "currency"
            ? {
                type: "currency" as const,
                symbol: row.currency_symbol,
              }
            : ({
                type: row.display_type,
              } as TableColumnConfigInputDisplayConfig);

      const config: TableColumnConfigInput = {
        display_name: row.display_name || null,
        edit_mode: row.edit_mode,
        display_config: displayConfig,
      };

      return [row.key, config];
    }),
  );
};

export const ColumnConfigModal: React.FC<ColumnConfigModalProps> = ({
  isOpen,
  onClose,
  afterLeave,
  columns,
  onSave,
  defaultColumnsEditable,
}) => {
  const form = useForm<ColumnConfigFormValues>({
    defaultValues: { columns: [], defaultColumnsEditable: true },
    mode: "onChange",
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "columns",
  });

  useEffect(() => {
    if (isOpen) {
      form.reset({
        columns: columnsToFormRows(columns),
        defaultColumnsEditable,
      });
    }
  }, [isOpen, form.reset]);

  return (
    <Modal afterLeave={afterLeave} open={isOpen} size="md" onClose={onClose}>
      <Modal.Header description="Set column names, types, and which columns are read-only.">
        Configure columns
      </Modal.Header>
      <Modal.Content>
        <div className="flex h-[540px] flex-col">
          <FormProvider {...form}>
            <div className="flex flex-1 flex-col gap-3 pb-4">
              {fields.length === 0 ? (
                <div className="flex flex-1 items-center justify-center">
                  <EmptyState
                    actionButton={{
                      label: "Add column",
                      icon: faPlus,
                      dataLoc: "column-config-add-button",
                      onClick: () => append(EMPTY_ROW),
                    }}
                    description="Add columns to configure their display names, types, and editability."
                    headline="No columns configured"
                    icon={faColumns}
                    size="compact"
                  />
                </div>
              ) : (
                <>
                  <Label mb="mb-0">Columns</Label>
                  <div className="flex items-end gap-2 font-inter-normal-12px text-gray-500">
                    <span className="flex flex-1 items-center gap-2">
                      Key
                      <Tooltip
                        body="The JSON field this column maps to"
                        placement="top"
                      >
                        <Icon icon={faCircleInfo} size="2xs" />
                      </Tooltip>
                    </span>
                    <span className="flex flex-1 items-center gap-2">
                      Column name
                      <Tooltip
                        body="Column label shown to reviewers"
                        placement="top"
                      >
                        <Icon icon={faCircleInfo} size="2xs" />
                      </Tooltip>
                    </span>
                    <span className="flex w-48 items-center gap-2">
                      Type
                      <Tooltip
                        body="Defines how values in this column are read. If unset, the type comes from the block input"
                        placement="top"
                      >
                        <Icon icon={faCircleInfo} size="2xs" />
                      </Tooltip>
                    </span>
                    <span className="flex w-16 items-center justify-center gap-2">
                      Editable
                      <Tooltip
                        body="Controls whether reviewers can edit this column during Case investigation"
                        placement="top"
                      >
                        <Icon icon={faCircleInfo} size="2xs" />
                      </Tooltip>
                    </span>
                    <div className="w-5" />
                  </div>

                  {fields.map((field, index) => (
                    <ColumnRow
                      key={field.id}
                      index={index}
                      totalFields={fields.length}
                      onRemove={() => remove(index)}
                    />
                  ))}

                  <div>
                    <Button
                      dataLoc="column-config-add-button"
                      iconLeft={faPlus}
                      size="sm"
                      variant="secondary"
                      onClick={() => append(EMPTY_ROW)}
                    >
                      Add column
                    </Button>
                  </div>
                </>
              )}
            </div>
            <DefaultColumnsEditableSwitch control={form.control} />
          </FormProvider>
        </div>
      </Modal.Content>
      <Modal.Footer
        primaryButton={
          <Button
            dataLoc="column-config-save-button"
            isDisabled={!form.formState.isValid || !form.formState.isDirty}
            variant="primary"
            onClick={form.handleSubmit((data) =>
              onSave(
                formRowsToColumns(data.columns),
                data.defaultColumnsEditable,
              ),
            )}
          >
            Save
          </Button>
        }
      />
    </Modal>
  );
};

type ColumnRowProps = {
  index: number;
  totalFields: number;
  onRemove: () => void;
};

const ColumnRow: React.FC<ColumnRowProps> = ({
  index,
  totalFields,
  onRemove,
}) => {
  const { control, register, getValues, setValue, getFieldState } =
    useFormContext<ColumnConfigFormValues>();
  const { errors } = useFormState({ control, name: `columns.${index}.key` });
  const keyError = errors.columns?.[index]?.key;

  return (
    <div className="flex items-start gap-2">
      <div className="flex min-w-0 flex-1 flex-col">
        <Input
          dataLoc={`column-config-key-${index}`}
          error={!!keyError}
          errorMessage={keyError?.message}
          placeholder="column_key"
          variant="monospaced"
          {...register(`columns.${index}.key`, {
            required: "Key is required",
            pattern: {
              value: /^[a-zA-Z_][a-zA-Z0-9_]*$/,
              message: "Letters, numbers and underscores only",
            },
            validate: {
              unique: (value) => {
                const allKeys = getValues("columns").map((r) => r.key);
                const duplicates = allKeys.filter((k) => k === value);
                return duplicates.length <= 1 || "Duplicate key";
              },
            },
            onChange: (e) => {
              const { isTouched } = getFieldState(
                `columns.${index}.display_name`,
              );

              if (!isTouched) {
                setValue(
                  `columns.${index}.display_name`,
                  unslugify(e.target.value),
                  { shouldTouch: false },
                );
              }
            },
            deps: Array.from({ length: totalFields }, (_, i) =>
              i !== index ? `columns.${i}.key` : "",
            ).filter(Boolean) as `columns.${number}.key`[],
          })}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <Input
          dataLoc={`column-config-display-name-${index}`}
          placeholder="Display name"
          {...register(`columns.${index}.display_name`)}
        />
      </div>

      <DisplayTypeSelect control={control} index={index} />
      <EditModeSwitch control={control} index={index} />

      <div className="flex items-center pt-1.5">
        <IconButton
          dataLoc={`column-config-remove-${index}`}
          icon={faTrashCan}
          size="sm"
          onClick={onRemove}
        />
      </div>
    </div>
  );
};

type ControlledFieldProps = {
  control: Control<ColumnConfigFormValues>;
  index: number;
};

const DisplayTypeSelect: React.FC<ControlledFieldProps> = ({
  control,
  index,
}) => {
  const { field: typeField } = useController({
    control,
    name: `columns.${index}.display_type`,
  });
  const { field: currencyField } = useController({
    control,
    name: `columns.${index}.currency_symbol`,
  });

  return (
    <div className="flex w-48 gap-1.5">
      <div className="min-w-0 flex-1">
        <Select
          dataLoc={`column-config-type-${index}`}
          options={DISPLAY_TYPE_OPTIONS}
          value={typeField.value}
          onChange={typeField.onChange}
        />
      </div>
      {typeField.value === "currency" && (
        <div className="w-16 shrink-0">
          <Select
            buttonRenderer={(option) => option?.displayValue}
            dataLoc={`column-config-currency-${index}`}
            options={CURRENCY_OPTIONS}
            value={currencyField.value}
            onChange={currencyField.onChange}
          />
        </div>
      )}
    </div>
  );
};

const EditModeSwitch: React.FC<ControlledFieldProps> = ({ control, index }) => {
  const { field } = useController({
    control,
    name: `columns.${index}.edit_mode`,
  });

  return (
    <div className="flex w-16 items-center justify-center pt-1.5">
      <Switch
        dataLoc={`column-config-editable-${index}`}
        enabled={field.value === TableEditMode.EDITABLE}
        onChange={(checked) =>
          field.onChange(
            checked ? TableEditMode.EDITABLE : TableEditMode.READ_ONLY,
          )
        }
      />
    </div>
  );
};

const DefaultColumnsEditableSwitch: React.FC<{
  control: Control<ColumnConfigFormValues>;
}> = ({ control }) => {
  const { field } = useController({
    control,
    name: "defaultColumnsEditable",
  });

  return (
    <div className="mt-auto border-gray-100 border-t pt-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col">
          <span className="font-inter-semibold-13px text-gray-800">
            Make non-configured columns editable by default
          </span>
          <span className="font-inter-normal-12px text-gray-500">
            Reviewers can edit every column you haven't configured to read-only.
          </span>
        </div>
        <Switch
          dataLoc="column-config-default-editable"
          enabled={field.value}
          onChange={field.onChange}
        />
      </div>
    </div>
  );
};
